// src/auth/unauthorizedReplay.ts - one renewal, one replay, when the api
// refuses a request's bearer.
//
// The api answers a refused bearer with 401 and code UNAUTHORIZED (legacy
// routes send no code). The token check runs before any handler, so that
// refusal means the server did nothing and sending the request again is safe:
// money ops carry their idempotency key on the replay too. A 401 with any other
// code (a wrong two-factor code, an expired pending sign-in) is about the
// request, and a renewal would not change its answer.
//
// UNAUTHORIZED does not prove the token check was the one that refused: the
// api's error handler answers every 401 with that code, so a handler that
// throws after the gates (the payout four-eyes rule) reads the same. By then the
// step-up gate has spent the operator's code, a replay is refused for reusing
// it, and the operator is told to wait for a new code and never hears the real
// reason. A request carrying a one-time proof is therefore never replayed: its
// 401 is final, and pressing again sends it with the current token.
//
// The console's own axios instance applies this in its response interceptor
// (services/apiService.ts). The `axios.create()` clients that bypass that
// interceptor install it with `installUnauthorizedReplay`.
import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { currentBearer, hasSession, refreshToken } from "./tokenProvider";

declare module "axios" {
  interface AxiosRequestConfig {
    /**
     * Set on the ONE replay of a request the api refused with 401: the token
     * was renewed once for it, and a second refusal is final.
     */
    unauthorizedRetry?: "replayed";
  }
}

const refusalCode = (data: unknown): string | undefined => {
  const code = (data as { code?: unknown } | null | undefined)?.code;
  return typeof code === "string" ? code : undefined;
};

/** The request headers that carry a proof the api spends when it reads it. */
const ONE_TIME_PROOF_HEADERS = ["X-2FA-Code", "X-Staff-Confirmation"];

const carriesOneTimeProof = (config: InternalAxiosRequestConfig): boolean =>
  ONE_TIME_PROOF_HEADERS.some((name) => Boolean(config.headers?.get?.(name)));

/**
 * True for a first 401 that may have refused the bearer, while a session
 * exists, on a request that can safely be sent twice.
 */
export const isRefusedBearer = (error: AxiosError): boolean => {
  if (!hasSession() || error.response?.status !== 401) return false;
  if (!error.config || error.config.unauthorizedRetry !== undefined) {
    return false;
  }
  if (carriesOneTimeProof(error.config)) return false;
  const code = refusalCode(error.response.data);
  return code === undefined || code === "UNAUTHORIZED";
};

const sentBearer = (config: InternalAxiosRequestConfig): string | undefined => {
  const value = config.headers?.get?.("Authorization");
  return typeof value === "string" ? value : undefined;
};

/**
 * The bearer the replay should carry. A request that left with a token the
 * session has since replaced (a renewal finished while it was in flight) only
 * needs the current one; otherwise the token is renewed once, shared with any
 * renewal already under way. Rejects when the renewal fails.
 */
export const bearerForReplay = async (
  config: InternalAxiosRequestConfig
): Promise<string | undefined> => {
  const current = await currentBearer();
  if (current && current !== sentBearer(config)) return current;
  await refreshToken();
  return currentBearer();
};

/** The refused request, marked as its one replay, with `bearer` or none. */
export const replayConfig = (
  config: InternalAxiosRequestConfig,
  bearer: string | undefined
): InternalAxiosRequestConfig => {
  if (bearer) config.headers.set("Authorization", bearer);
  else config.headers.delete("Authorization");
  return { ...config, unauthorizedRetry: "replayed" };
};

/**
 * For an `axios.create()` client that bypasses the shared interceptor. Any
 * other error passes through untouched, a failed renewal rejects with the
 * ORIGINAL refusal, and the replay's own outcome is the answer, so the
 * client's error mapping sees exactly what it saw before.
 */
export const installUnauthorizedReplay = (client: AxiosInstance): void => {
  // Optional chaining as in apiService: some suites replace axios with a bare mock.
  client?.interceptors?.response?.use?.(
    undefined,
    async (error: unknown): Promise<AxiosResponse> => {
      if (!axios.isAxiosError(error) || !isRefusedBearer(error)) throw error;
      const config = error.config!;
      let bearer: string | undefined;
      try {
        bearer = await bearerForReplay(config);
      } catch {
        throw error;
      }
      return client.request(replayConfig(config, bearer));
    }
  );
};
